import { useEffect } from 'react';
import { Content, Model } from '@shared/types';
import { OutputFormat } from '@/lib/outputFormatCache';
import { INLET_LABELS } from '@/config/supin';
import { SUSP_TYPE_LABELS } from '@/config/shark';
import {
  IMPLICIT_PRIMITIVE_LABELS,
  ImplicitPrimitive,
  ImplicitPrimitiveParams,
  LATTICE_FILL_DEFAULTS,
} from '@/config/implicitPrimitives';
import type { HomepageContextValue } from '@/contexts/HomepageContext';

type TubeframeParams = NonNullable<HomepageContextValue['tubeframeParams']>;
type SupinParams = NonNullable<HomepageContextValue['supinParams']>;
type SuspensionKinematicsParams = NonNullable<
  HomepageContextValue['suspensionKinematicsParams']
>;

interface UsePromptSidebarActionsArgs {
  isMobile: boolean;
  vehicleEnabled: boolean;
  vehicleTab: string;
  vehicleSubTab: string;
  textToCadSelected: boolean;
  tubeframeParamsReady: boolean;
  tubeframeParams: TubeframeParams | null;
  supinParamsReady: boolean;
  supinParams: SupinParams | null;
  suspensionKinematicsParamsReady: boolean;
  suspensionKinematicsParams: SuspensionKinematicsParams | null;
  isTubeframeJoint: boolean;
  isSupinInlet: boolean;
  isSuspensionKinematics: boolean;
  isVehicleImplicitPrimitive: boolean;
  implicitPrimitive: ImplicitPrimitive;
  implicitPrimitiveParams: ImplicitPrimitiveParams;
  model: Model;
  outputFormat: OutputFormat;
  isGenerating: boolean;
  limitReached: boolean;
  /** True while a SUPIN inlet is selected but its license password has not been
   *  entered — disables the SUPIN Generate action until the gate is unlocked. */
  supinLicenseBlocked: boolean;
  handleGenerate: (content: Content) => void;
  registerSidebarAction: HomepageContextValue['registerSidebarAction'];
}

export function usePromptSidebarActions({
  isMobile,
  vehicleEnabled,
  vehicleTab,
  vehicleSubTab,
  textToCadSelected,
  tubeframeParamsReady,
  tubeframeParams,
  supinParamsReady,
  supinParams,
  suspensionKinematicsParamsReady,
  suspensionKinematicsParams,
  isTubeframeJoint,
  isSupinInlet,
  isSuspensionKinematics,
  isVehicleImplicitPrimitive,
  implicitPrimitive,
  implicitPrimitiveParams,
  model,
  outputFormat,
  isGenerating,
  limitReached,
  supinLicenseBlocked,
  handleGenerate,
  registerSidebarAction,
}: UsePromptSidebarActionsArgs) {
  // Register vehicle domain generate action for the sidebar button (desktop only)
  useEffect(() => {
    if (isMobile || !vehicleEnabled) return;
    // Land → Wheels / Space → Heat Exchanger have their own registration below
    // (implicit-primitive pipeline). Bailing here keeps this effect from
    // overwriting that one.
    if (isVehicleImplicitPrimitive) return;
    const vehicleParamsReady =
      (vehicleEnabled &&
        vehicleTab === 'space' &&
        vehicleSubTab === 'rocket-nozzle') ||
      tubeframeParamsReady ||
      supinParamsReady ||
      suspensionKinematicsParamsReady;
    const fireFn = () => {
      if (isTubeframeJoint && tubeframeParams) {
        const { od, id, matingSurface, units } = tubeframeParams;
        const surfaceLabel: Record<string, string> = {
          'straight-wall': 'Straight Wall',
          lattice: 'Lattice',
          threaded: 'Threaded',
        };
        const unitSuffix = units === 'in' ? '"' : 'mm';
        handleGenerate({
          vehicleParams: tubeframeParams,
          text: `Design a Tubeframe Joint with ${od}${unitSuffix} OD ${id}${unitSuffix} ID and ${surfaceLabel[matingSurface] ?? matingSurface} Mating Surface`,
          model,
          outputFormat,
        });
      } else if (isSupinInlet && supinParams) {
        // License gate: never fire a SUPIN run until the access password has
        // been entered, even if the button is somehow reached while blocked.
        if (supinLicenseBlocked) return;
        handleGenerate({
          supinParams,
          outputFormat: 'solidbody',
          text: `Generate NASA SUPIN ${INLET_LABELS[supinParams.inletType] ?? supinParams.inletType} supersonic inlet at Mach ${supinParams.mach}, ${supinParams.altitude} ft`,
          model,
        });
      } else if (isSuspensionKinematics && suspensionKinematicsParams) {
        handleGenerate({
          suspensionKinematicsParams,
          outputFormat: 'solidbody',
          text: `Analyze ${suspensionKinematicsParams.axle} ${SUSP_TYPE_LABELS[suspensionKinematicsParams.suspType] ?? suspensionKinematicsParams.suspType} suspension kinematics`,
          model,
        });
      } else if (vehicleTab === 'space' && vehicleSubTab === 'rocket-nozzle') {
        handleGenerate({
          text: 'Design a converging-diverging de Laval rocket nozzle',
          model,
          outputFormat,
        });
      }
    };
    registerSidebarAction({
      fn: fireFn,
      // SUPIN stays disabled until its license gate is unlocked.
      enabled: vehicleParamsReady && !limitReached && !supinLicenseBlocked,
      loading: isGenerating,
    });
    return () => registerSidebarAction({ fn: null, enabled: false });
  }, [
    isMobile,
    vehicleEnabled,
    vehicleTab,
    vehicleSubTab,
    tubeframeParamsReady,
    supinParamsReady,
    suspensionKinematicsParamsReady,
    isTubeframeJoint,
    isSupinInlet,
    isSuspensionKinematics,
    isVehicleImplicitPrimitive,
    tubeframeParams,
    supinParams,
    suspensionKinematicsParams,
    isGenerating,
    limitReached,
    supinLicenseBlocked,
    model,
    outputFormat,
    handleGenerate,
    registerSidebarAction,
  ]);

  // Sidebar Generate action for the Implicit Primitives tab. Lives under
  // text-to-cad (NOT vehicleEnabled), so it has its own registration block.
  // Defaults are always populated in HomepageContext, so the button is
  // always enabled — there's no "fill these in first" state to gate on.
  const isImplicitPrimitives =
    textToCadSelected && vehicleSubTab === 'implicit-primitives';
  useEffect(() => {
    if (isMobile || !isImplicitPrimitives) return;
    const fireFn = () => {
      const label = IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive];
      // implicitPrimitiveParams is what makes implicit-chat skip the LLM
      // entirely and call /primitives/<kind> directly — see the short-circuit
      // block at the top of that edge function. text is just for display.
      handleGenerate({
        text: `Generate ${label}`,
        model,
        outputFormat: 'implicit',
        implicitPrimitiveParams: {
          kind: implicitPrimitiveParams.kind,
          params: implicitPrimitiveParams.params as unknown as Record<
            string,
            string
          >,
        },
      });
    };
    registerSidebarAction({
      fn: fireFn,
      enabled: !limitReached,
      loading: isGenerating,
    });
    return () => registerSidebarAction({ fn: null, enabled: false });
  }, [
    isMobile,
    isImplicitPrimitives,
    implicitPrimitive,
    implicitPrimitiveParams,
    model,
    isGenerating,
    limitReached,
    handleGenerate,
    registerSidebarAction,
  ]);

  // Sidebar Generate action for the NASA Lattice Primitives tab. Mirrors
  // the implicit-primitives effect above but routes through outputFormat
  // 'unitcell' → unitcell-chat → unitcellengine Python sidecar. The
  // topology slug is carried inside `params` (not a sibling field) to
  // avoid bumping the shared Content type for one path; the edge function
  // pulls it out of params before forwarding to the microservice.
  const isNasaLatticePrimitives =
    textToCadSelected && vehicleSubTab === 'nasa-lattice-primitives';
  useEffect(() => {
    if (isMobile || !isNasaLatticePrimitives) return;
    if (implicitPrimitiveParams.kind !== 'nasa-lattice') return;
    const fireFn = () => {
      const label = IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive];
      handleGenerate({
        text: `Generate ${label}`,
        model,
        outputFormat: 'unitcell',
        implicitPrimitiveParams: {
          kind: 'nasa-lattice',
          // Inject the topology slug into params so the wire shape
          // matches { kind, params: Record<string, string> } — the edge
          // function reads params.topology back out.
          params: {
            topology: implicitPrimitiveParams.topology,
            ...(implicitPrimitiveParams.params as unknown as Record<
              string,
              string
            >),
          },
        },
      });
    };
    registerSidebarAction({
      fn: fireFn,
      enabled: !limitReached,
      loading: isGenerating,
    });
    return () => registerSidebarAction({ fn: null, enabled: false });
  }, [
    isMobile,
    isNasaLatticePrimitives,
    implicitPrimitive,
    implicitPrimitiveParams,
    model,
    isGenerating,
    limitReached,
    handleGenerate,
    registerSidebarAction,
  ]);

  // Sidebar Generate action for the NASA Unitcell Lattice Converter tab. Carries
  // the 'lattice-fill' params (lattice style + uploaded part ref) and routes
  // through outputFormat 'unitcell' — the converter shares the unitcell kernel
  // lock, and the edge function branches on kind === 'lattice-fill' to the
  // PicoGK fill endpoint. Generate stays disabled until a part is uploaded
  // (params.meshRef set), so a user can't fire a fill with no input geometry.
  const isUnitcellConverter =
    textToCadSelected && vehicleSubTab === 'nasa-unitcell-converter';
  useEffect(() => {
    if (isMobile || !isUnitcellConverter) return;
    // Always register while the converter tab is active — even before a lattice
    // style is picked (kind still the previous primitive). Early-returning here
    // left the PREVIOUS tab's registration in place, so the sidebar Generate
    // button stayed enabled before any part was uploaded. Falling back to
    // LATTICE_FILL_DEFAULTS (meshRef '') guarantees the button is DISABLED until
    // a part is actually uploaded (meshRef set). #309.
    const p =
      implicitPrimitiveParams.kind === 'lattice-fill'
        ? implicitPrimitiveParams.params
        : LATTICE_FILL_DEFAULTS;
    const fireFn = () => {
      const label = IMPLICIT_PRIMITIVE_LABELS[p.topology];
      handleGenerate({
        text: `Convert uploaded part to ${label} lattice`,
        model,
        outputFormat: 'unitcell',
        implicitPrimitiveParams: {
          kind: 'lattice-fill',
          params: p as unknown as Record<string, string>,
        },
      });
    };
    registerSidebarAction({
      fn: fireFn,
      enabled: !limitReached && p.meshRef !== '',
      loading: isGenerating,
    });
    return () => registerSidebarAction({ fn: null, enabled: false });
  }, [
    isMobile,
    isUnitcellConverter,
    implicitPrimitiveParams,
    model,
    isGenerating,
    limitReached,
    handleGenerate,
    registerSidebarAction,
  ]);

  // Sidebar Generate action for Land → Wheels. Shares the implicit pipeline
  // with the Implicit Primitives library — same params shape, same
  // edge-function short-circuit — but registered separately because it sits
  // under a vehicle domain rather than text-to-cad.
  useEffect(() => {
    if (isMobile || !isVehicleImplicitPrimitive) return;
    const fireFn = () => {
      const label = IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive];
      handleGenerate({
        text: `Generate ${label}`,
        model,
        outputFormat: 'implicit',
        implicitPrimitiveParams: {
          kind: implicitPrimitiveParams.kind,
          params: implicitPrimitiveParams.params as unknown as Record<
            string,
            string
          >,
        },
      });
    };
    registerSidebarAction({
      fn: fireFn,
      enabled: !limitReached,
      loading: isGenerating,
    });
    return () => registerSidebarAction({ fn: null, enabled: false });
  }, [
    isMobile,
    isVehicleImplicitPrimitive,
    implicitPrimitive,
    implicitPrimitiveParams,
    model,
    isGenerating,
    limitReached,
    handleGenerate,
    registerSidebarAction,
  ]);
}
